import React, { createContext, useContext } from "react";

/**
 * Runtime bridge while EEGViewer.tsx is split into focused modules. New code
 * should depend on the typed slice hooks below instead of adding flat keys to
 * the legacy bridge consumed by EEGPlaybackCore and EEGViewerDeferredChrome.
 */
export type EEGViewerRuntimeSliceName =
  | "playback"
  | "montage"
  | "tiles"
  | "chrome"
  | "annotations";

export type EEGViewerRuntimeSlice<TName extends EEGViewerRuntimeSliceName> = {
  slice: TName;
};

export type EEGPlaybackRuntimeSlice = EEGViewerRuntimeSlice<"playback">;
export type EEGMontageRuntimeSlice = EEGViewerRuntimeSlice<"montage">;
export type EEGTileRuntimeSlice = EEGViewerRuntimeSlice<"tiles">;
export type EEGChromeRuntimeSlice = EEGViewerRuntimeSlice<"chrome">;
export type EEGAnnotationRuntimeSlice = EEGViewerRuntimeSlice<"annotations">;

export type EEGViewerRuntimeContextValue = object;

const EEG_VIEWER_RUNTIME_CONTEXT_KEY =
  "__NERVEDX_EEG_VIEWER_RUNTIME_CONTEXT__";

type EEGViewerRuntimeGlobal = typeof globalThis & {
  [EEG_VIEWER_RUNTIME_CONTEXT_KEY]?:
    | React.Context<EEGViewerRuntimeContextValue | null>
    | undefined;
};

const eegViewerRuntimeGlobal = globalThis as EEGViewerRuntimeGlobal;

const EEGViewerRuntimeContext =
  eegViewerRuntimeGlobal[EEG_VIEWER_RUNTIME_CONTEXT_KEY] ??
  (eegViewerRuntimeGlobal[EEG_VIEWER_RUNTIME_CONTEXT_KEY] =
    createContext<EEGViewerRuntimeContextValue | null>(null));

export function EEGViewerRuntimeProvider({
  value,
  children,
}: {
  value: EEGViewerRuntimeContextValue;
  children: React.ReactNode;
}) {
  return (
    <EEGViewerRuntimeContext.Provider value={value}>
      {children}
    </EEGViewerRuntimeContext.Provider>
  );
}

export function useEEGViewerRuntime<
  T extends EEGViewerRuntimeContextValue = EEGViewerRuntimeContextValue,
>() {
  const ctx = useContext(EEGViewerRuntimeContext);
  if (!ctx) {
    throw new Error("useEEGViewerRuntime requires EEGViewerRuntimeProvider");
  }
  return ctx as T;
}

function useEEGRuntimeSlice<TSlice extends EEGViewerRuntimeContextValue>(
  name: EEGViewerRuntimeSliceName,
) {
  const runtime = useEEGViewerRuntime<Record<string, unknown>>();
  const slice = runtime[name];
  if (slice && typeof slice === "object") {
    return slice as TSlice;
  }
  return runtime as TSlice;
}

export function useEEGPlaybackRuntime<
  TSlice extends EEGViewerRuntimeContextValue = EEGPlaybackRuntimeSlice,
>() {
  return useEEGRuntimeSlice<TSlice>("playback");
}

export function useEEGMontageRuntime<
  TSlice extends EEGViewerRuntimeContextValue = EEGMontageRuntimeSlice,
>() {
  return useEEGRuntimeSlice<TSlice>("montage");
}

export function useEEGTileRuntime<
  TSlice extends EEGViewerRuntimeContextValue = EEGTileRuntimeSlice,
>() {
  return useEEGRuntimeSlice<TSlice>("tiles");
}

export function useEEGChromeRuntime<
  TSlice extends EEGViewerRuntimeContextValue = EEGChromeRuntimeSlice,
>() {
  return useEEGRuntimeSlice<TSlice>("chrome");
}

export function useEEGAnnotationRuntime<
  TSlice extends EEGViewerRuntimeContextValue = EEGAnnotationRuntimeSlice,
>() {
  return useEEGRuntimeSlice<TSlice>("annotations");
}
